import React, { useState, useEffect } from 'react';
import FAQFeedback from '../components/Faqs'; // Your frontend component
import axios from "axios";
import config from '../config/config';

const FaqAdmin = ({ slug = '', onSave, onClose }) => {
  const token = localStorage.getItem("token");

  // Default config
  const defaultConfig = {
    title: "Frequently Asked Questions",
    subtitle: "Find answers to the most common questions about our products and services.",
    faqs: [], // Populated from selected
    devMode: false,
    customStyles: {
      container: {
        padding: 'py-12 md:py-20',
        background: 'bg-white',
        titleColor: '#111827',
        subtitleColor: '#4B5563',
        questionColor: '#111827',
        answerColor: '#374151',
        borderColor: '#e5e7eb',
        hoverBg: '#f9fafb',
      },
    },
  };

  // States
  const [title, setTitle] = useState(defaultConfig.title);
  const [subtitle, setSubtitle] = useState(defaultConfig.subtitle);
  const [faqs, setFaqs] = useState(defaultConfig.faqs);
  const [devMode, setDevMode] = useState(defaultConfig.devMode);
  const [container, setContainer] = useState(defaultConfig.customStyles.container);

  const [allFaqs, setAllFaqs] = useState([]); // All FAQs from backend
  const [selectedFaqIds, setSelectedFaqIds] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fullScreenPreview, setFullScreenPreview] = useState(false);

  // Load saved config
  useEffect(() => {
    const loadFaqSettings = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

        let response;
        const isDuplicate = slug.includes('-');

        if (isDuplicate) {
          response = await axios.get(
            `${config.API_URL}/api/pullCustomSettings/${slug}`,
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        } else {
          response = await axios.get(
            config.API_URL + '/api/pullFaqSettings',
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        }

        const dbData = isDuplicate
          ? (response.data.customSettings || {})
          : (response.data.faqSettings || {});

        setTitle(dbData.title || defaultConfig.title);
        setSubtitle(dbData.subtitle || defaultConfig.subtitle);
        setFaqs(dbData.faqs || []);
        setDevMode(dbData.devMode || defaultConfig.devMode);
        setContainer(dbData.customStyles?.container || defaultConfig.customStyles.container);
      } catch (error) {
        console.error("Failed to load FAQ settings:", error);
      } finally {
        setLoading(false);
      }
    };

    loadFaqSettings();
  }, [token, slug]);

  // Fetch ALL available FAQs for modal
  const fetchAllFaqs = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/api/pullFaqs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllFaqs(response.data.faqs || []);
    } catch (err) {
      console.error("Failed to fetch all FAQs:", err);
    }
  };

  useEffect(() => {
    if (token) fetchAllFaqs();
  }, [token]);

  // Current config
  const currentConfig = {
    title,
    subtitle,
    faqs,
    devMode,
    customStyles: { container },
  };

  // Save
  const saveFaqSettings = async () => {
    try {
      setSaving(true);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const endpoint = slug ? `/api/saveFaqSettings/${slug}` : '/api/saveFaqSettings';

      const response = await axios.post(
        config.API_URL + endpoint,
        { settings: currentConfig },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 1) {
        alert("FAQs saved successfully! 🎉");
        if (onSave) onSave(currentConfig);
      } else {
        alert("Save failed.");
      }
    } catch (error) {
      console.error("Save error:", error);
      alert("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  // Modal
  const openAddFaqsModal = () => {
    setSearchQuery('');
    setSelectedFaqIds([]);
    setIsModalOpen(true);
  };

  const toggleFaqSelection = (faqId) => {
    setSelectedFaqIds(prev =>
      prev.includes(faqId)
        ? prev.filter(id => id !== faqId)
        : [...prev, faqId]
    );
  };

  const confirmAddFaqs = () => {
    const newFaqs = allFaqs
      .filter(f => selectedFaqIds.includes(f.id))
      .map(f => ({
        id: f.id,
        question: f.question,
        answer: f.answer,
        category: f.category || 'general',
        isOpen: false,
      }));

    const existingIds = new Set(faqs.map(f => f.id));
    const filteredNew = newFaqs.filter(f => !existingIds.has(f.id));

    setFaqs(prev => [...prev, ...filteredNew]);
    setIsModalOpen(false);
  };

  // Reorder & Remove
  const moveFaq = (id, direction) => {
    const index = faqs.findIndex(f => f.id === id);
    if ((direction === -1 && index === 0) || (direction === 1 && index === faqs.length - 1)) return;

    const newList = [...faqs];
    [newList[index], newList[index + direction]] = [newList[index + direction], newList[index]];
    setFaqs(newList);
  };

  const removeFaq = (id) => {
    setFaqs(faqs.filter(f => f.id !== id));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading FAQ settings...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Preview */}
      <div className="flex-1 relative">
        <div className="absolute inset-0 flex flex-col">
          <div className="bg-white border-b px-6 py-4 flex justify-between items-center shadow-sm">
            <h1 className="text-2xl font-bold text-gray-900">FAQ Section Editor</h1>
            <button
              onClick={() => setFullScreenPreview(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700"
            >
              Full Screen Preview
            </button>
          </div>
          <div className="flex-1 overflow-auto bg-gray-50 p-8">
            <FAQFeedback config={currentConfig} />
          </div>
        </div>
      </div>

      {/* Settings Drawer */}
      <div className="w-96 bg-white border-l overflow-y-auto shadow-2xl relative">
        <div className="p-6 space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">FAQ Settings</h2>

          {/* Title & Subtitle */}
          <div className="space-y-3">
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-4 py-2 border rounded-md" placeholder="Section Title" />
            <textarea value={subtitle} onChange={e => setSubtitle(e.target.value)} className="w-full px-4 py-2 border rounded-md h-20" placeholder="Subtitle" />
          </div>

          {/* Selected FAQs */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Selected FAQs ({faqs.length})</h3>
              <button onClick={openAddFaqsModal} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700">
                + Add FAQs
              </button>
            </div>

            {faqs.map((f) => (
              <div key={f.id} className="border rounded-lg p-4 bg-gray-50 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium line-clamp-1">{f.question}</span>
                  <div className="flex gap-1">
                    <button onClick={() => moveFaq(f.id, -1)} disabled={faqs.findIndex(item => item.id === f.id) === 0} className="p-1 hover:bg-gray-200 rounded disabled:opacity-50">↑</button>
                    <button onClick={() => moveFaq(f.id, 1)} disabled={faqs.findIndex(item => item.id === f.id) === faqs.length - 1} className="p-1 hover:bg-gray-200 rounded disabled:opacity-50">↓</button>
                    <button onClick={() => removeFaq(f.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">×</button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 line-clamp-2">{f.answer}</p>
              </div>
            ))}
          </div>

          {/* Dev Mode */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-yellow-900">Dev Mode – Custom Styles</h3>
              <button onClick={() => setDevMode(!devMode)} className={`relative inline-flex h-6 w-11 rounded-full transition ${devMode ? 'bg-yellow-600' : 'bg-gray-300'}`}>
                <span className={`inline-block h-4 w-4 rounded-full bg-white transition ${devMode ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {devMode && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Padding (Tailwind)</label>
                  <input value={container.padding} onChange={e => setContainer({...container, padding: e.target.value})} placeholder="py-12 md:py-20" className="w-full px-3 py-2 border rounded-md text-sm" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Background</label>
                  <input value={container.background} onChange={e => setContainer({...container, background: e.target.value})} placeholder="bg-white OR #ffffff OR url(...)" className="w-full px-3 py-2 border rounded-md text-sm" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload Background Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      const formData = new FormData();
                      formData.append('image', file);

                      try {
                        const res = await axios.post(
                          `${config.API_URL}/api/upload-image`,
                          formData,
                          { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
                        );

                        if (res.data.url) {
                          setContainer(prev => ({ ...prev, background: `url(${res.data.url})` }));
                          alert("Background image uploaded!");
                        }
                      } catch (err) {
                        alert("Upload failed");
                      }
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title Color</label>
                    <input type="color" value={container.titleColor} onChange={e => setContainer({...container, titleColor: e.target.value})} className="w-full h-10 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle Color</label>
                    <input type="color" value={container.subtitleColor} onChange={e => setContainer({...container, subtitleColor: e.target.value})} className="w-full h-10 rounded" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Question Color</label>
                    <input type="color" value={container.questionColor} onChange={e => setContainer({...container, questionColor: e.target.value})} className="w-full h-10 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Answer Color</label>
                    <input type="color" value={container.answerColor} onChange={e => setContainer({...container, answerColor: e.target.value})} className="w-full h-10 rounded" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Border/Hover Color</label>
                  <input type="color" value={container.borderColor} onChange={e => setContainer({...container, borderColor: e.target.value})} className="w-full h-10 rounded" />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="fixed bottom-0 right-0 w-96 bg-white border-t p-6 shadow-lg">
          <button
            onClick={saveFaqSettings}
            disabled={saving}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 disabled:opacity-70 shadow"
          >
            {saving ? 'Saving...' : 'Save & Publish FAQs'}
          </button>
        </div>
      </div>

      {/* Add FAQs Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto flex flex-col">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Add FAQs</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-900 text-3xl">×</button>
            </div>

            <div className="p-6">
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search FAQs..."
                className="w-full px-4 py-2 border rounded-md mb-6"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {allFaqs
                  .filter(f => searchQuery === '' || f.question.toLowerCase().includes(searchQuery.toLowerCase()) || f.answer.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map(f => (
                    <div
                      key={f.id}
                      className={`p-4 border rounded-lg cursor-pointer transition ${
                        selectedFaqIds.includes(f.id) ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => toggleFaqSelection(f.id)}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedFaqIds.includes(f.id)}
                          onChange={() => {}}
                          onClick={e => e.stopPropagation()}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{f.question}</p>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{f.answer}</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {allFaqs.filter(f => searchQuery === '' || f.question.toLowerCase().includes(searchQuery.toLowerCase()) || f.answer.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
                <p className="text-center text-gray-500 mt-8">No FAQs found</p>
              )}
            </div>

            <div className="border-t px-6 py-4 flex justify-end gap-4 sticky bottom-0 bg-white">
              <button onClick={() => setIsModalOpen(false)} className="px-6 py-2 border rounded-lg hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={confirmAddFaqs}
                disabled={selectedFaqIds.length === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Add {selectedFaqIds.length} FAQ{selectedFaqIds.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Preview */}
      {fullScreenPreview && (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" onClick={() => setFullScreenPreview(false)}>
          <div className="bg-white/10 backdrop-blur px-6 py-4 flex justify-end">
            <button className="text-white text-4xl hover:text-gray-300">×</button>
          </div>
          <div className="flex-1 overflow-auto p-8" onClick={e => e.stopPropagation()}>
            <FAQFeedback config={currentConfig} />
          </div>
        </div>
      )}
    </div>
  );
};

export default FaqAdmin;